import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchCollectionsWorklist } from '../api';
import { formatCurrency } from '../utils/currency';

/**
 * Firm-wide collections worklist. [H5]
 *
 * Dunning used to be driveable only one invoice at a time, from inside the
 * invoice drawer — so nobody could see the overdue book as a book, and an
 * invoice nobody happened to open was never chased.
 *
 * The figures come from reports/collections-worklist/, which is built on the
 * same build_ar_aging() the aged-receivables report uses, so this view and
 * that one cannot disagree about who owes what.
 */
const FILTERS = [
  { key: 'chaseable', label: 'Ready to chase' },
  { key: 'all', label: 'All overdue' },
  { key: 'in_grace', label: 'In grace' },
  { key: 'exhausted', label: 'Dunning exhausted' },
];

export default function CollectionsWorklist({ authAxios }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('chaseable');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchCollectionsWorklist(authAxios));
    } catch (err) {
      console.error('Failed to load the collections worklist', err);
      setError(err?.response?.data?.detail || 'Could not load the collections worklist.');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = data?.invoices || [];
    if (filter === 'all') return all;
    if (filter === 'chaseable') return all.filter(r => r.chaseable);
    if (filter === 'in_grace') return all.filter(r => r.in_grace_period);
    return all.filter(r => r.dunning_exhausted);
  }, [data, filter]);

  if (loading) {
    return <div className="p-6 text-gray-500">Loading collections worklist…</div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 mb-1">Collections Worklist</h2>
          <p className="text-gray-600">
            Overdue debt across the firm &bull; As at {data?.as_at}
          </p>
        </div>
        <button
          type="button" onClick={load}
          className="text-sm px-3 py-1.5 rounded border bg-white hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div role="alert" className="mb-4 border border-red-300 bg-red-50 text-red-800 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {data && !data.collections_enabled && (
        <div role="alert" className="mb-4 border border-amber-300 bg-amber-50 text-amber-900 rounded-lg px-4 py-3 text-sm">
          Automated dunning is switched off for this firm, so no reminders are being
          sent. The debt below is still outstanding and can be chased manually.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <p className="text-xs text-gray-500 uppercase">Total overdue</p>
          <p className="text-2xl font-bold text-red-700">{formatCurrency(data?.total_overdue)}</p>
          <p className="text-xs text-gray-400">{data?.counts?.overdue_invoices || 0} invoices</p>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <p className="text-xs text-gray-500 uppercase">Ready to chase</p>
          <p className="text-2xl font-bold text-orange-700">{data?.counts?.chaseable_now || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <p className="text-xs text-gray-500 uppercase">In grace period</p>
          <p className="text-2xl font-bold text-gray-700">{data?.counts?.in_grace || 0}</p>
          <p className="text-xs text-gray-400">{data?.grace_days} day grace</p>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <p className="text-xs text-gray-500 uppercase">Dunning exhausted</p>
          <p className="text-2xl font-bold text-purple-700">{data?.counts?.exhausted || 0}</p>
          <p className="text-xs text-gray-400">after {data?.max_stages} stages</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f.key} type="button" onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded text-sm border ${
              filter === f.key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Invoice', 'Client', 'Balance', 'Days overdue', 'Stage',
                'Last reminder', 'Next action'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  Nothing in this list.
                </td>
              </tr>
            )}
            {rows.map(row => (
              <tr key={row.invoice_id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.invoice_number}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{row.client}</td>
                <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatCurrency(row.balance)}</td>
                <td className="px-4 py-3 text-sm">
                  <span className={row.days_overdue > 90 ? 'text-red-700 font-semibold' : 'text-gray-700'}>
                    {row.days_overdue}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{row.dunning_stage}</td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {row.last_dunning_sent
                    ? `${row.days_since_last_reminder}d ago`
                    : <span className="text-gray-400">Never</span>}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={
                    row.chaseable ? 'text-orange-700 font-medium'
                      : row.dunning_exhausted ? 'text-purple-700' : 'text-gray-500'
                  }>
                    {row.next_action}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
